/**
 * PATH: packages/email-hub/src/feature/lib/dateBuckets.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-DATEBUCKETS-001
 * PURPOSE: Pure date→bucket classifier for the inbox list's sticky time
 *          section headers (Today / Yesterday / This week / This month /
 *          Older).
 * WHY:     Operator 2026-07-08: "I still see the infinite scroll here" —
 *          the list rendered as one undifferentiated stream. Buckets give
 *          the scroll a visible structure without changing row order,
 *          pagination, selection or keyboard nav (headers are inert
 *          siblings, mirroring the mailbox-group header pattern).
 * FLOW:    (received_at ISO, now) → bucket key → label.
 * RELATED:
 *   - feature/pages/InboxPage.tsx: emits a header when a row's bucket
 *     differs from the previous row's (newest-first order only — any
 *     other sort would interleave buckets and the headers would repeat).
 */

export type DateBucket = 'today' | 'yesterday' | 'week' | 'month' | 'older'

export const DATE_BUCKET_LABELS: Record<DateBucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This week',
  month: 'This month',
  older: 'Older',
}

/** Midnight (local) of the given date — buckets are calendar-day based so
 *  "Yesterday" means the previous calendar day, not "24–48h ago". */
function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * dateBucketOf(receivedAt, now?) → the bucket the timestamp falls in.
 *
 * Defensive: unparseable/missing timestamps classify as 'older' (they sort
 * to the bottom under newest-first, so this keeps header emission
 * monotonic). "This week" = the last 7 calendar days (excluding today and
 * yesterday); "This month" = the last 31 calendar days. Calendar-day
 * windows rather than ISO week/month boundaries: cheaper, timezone-honest
 * (local midnight), and the label reads correctly either way.
 */
export function dateBucketOf(receivedAt: string, now: Date = new Date()): DateBucket {
  const t = new Date(receivedAt).getTime()
  if (!Number.isFinite(t)) return 'older'
  const todayStart = startOfDay(now)
  if (t >= todayStart) return 'today'
  if (t >= todayStart - DAY_MS) return 'yesterday'
  if (t >= todayStart - 7 * DAY_MS) return 'week'
  if (t >= todayStart - 31 * DAY_MS) return 'month'
  return 'older'
}
